/**
 * Template List Component
 * Displays list of prompt templates with actions
 */

import { Star, Edit2, Trash2, Copy } from 'lucide-react';
import clsx from 'clsx';
import type { PromptTemplate } from '@/types/promptTemplates';

interface TemplateListProps {
  templates: PromptTemplate[];
  onSelect: (template: PromptTemplate) => void;
  onDelete: (template: PromptTemplate) => void;
  onSetDefault: (template: PromptTemplate) => void;
  onDuplicate: (template: PromptTemplate) => void;
}

export function TemplateList({
  templates,
  onSelect,
  onDelete,
  onSetDefault,
  onDuplicate,
}: TemplateListProps) {
  if (templates.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No templates found. Create one to get started.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {templates.map(template => (
        <div
          key={template.id}
          className={clsx(
            'bg-white border rounded-lg p-4 hover:border-primary-300 transition-colors cursor-pointer',
            template.is_default ? 'border-yellow-300 bg-yellow-50/50' : 'border-gray-200'
          )}
          onClick={() => onSelect(template)}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-gray-900">{template.name}</h3>
                {template.is_default && (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                    <Star className="w-3 h-3" />
                    Default
                  </span>
                )}
              </div>
              {template.description && (
                <p className="text-sm text-gray-500 mt-1 truncate">{template.description}</p>
              )}
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                <span>{template.sections.length} sections</span>
                <span>Updated {new Date(template.updated_at).toLocaleDateString()}</span>
              </div>
            </div>

            <div className="flex items-center gap-1 ml-4" onClick={e => e.stopPropagation()}>
              {!template.is_default && (
                <button
                  onClick={() => onSetDefault(template)}
                  className="p-2 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded"
                  title="Set as Default"
                >
                  <Star className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => onDuplicate(template)}
                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                title="Duplicate"
              >
                <Copy className="w-4 h-4" />
              </button>
              <button
                onClick={() => onSelect(template)}
                className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                title="Edit"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              {!template.is_default && (
                <button
                  onClick={() => onDelete(template)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
